import React, { useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useUserInfo } from '@lib/user-context';
import { gql } from '@apollo/client';
import { graphql } from '@lib/api';
import { AGENT_BY_ID_QUERY } from '@lib/fragments';

// ---------------------------------------------------------
import classNames from 'classnames';
import Listing from '@lib/models/listing';
import { truncateListingName } from '@lib/utils';

import { execAssociatedAgentQuery } from '@lib/associatedAgent';

import { useSibcyListing } from '@lib/sibcy-listing-context';
import { useLeadsPlusAgentData } from '@lib/hooks';

// ---------------------------------------------------------

import Layout from '@layout/index';
import Container from '@layout/container';
import Image from '@components/image';
import Button from '@components/button';
import PostList from '@components/post-list';
import SellWithSibcy from '@components/sell-with-sibcy';
import SimilarProperties from '@components/similar-properties';
import FaqBlock from '@components/faq-block';
import MortgageCalculatorEnhanced from '@components/calculators/mortgage-calculator-enhanced';
import TopicCarousel from '@components/topic-carousel';
import Link from '@components/link';
import { pushDataLayerEvent } from '@lib/gtm';
import { buildListingFaqItems } from '@lib/faq-content';
import { generateFAQSchema } from '@lib/generate-schemas';

// ---------------------------------------------------------

import PropertyHeader from './components/property-header';
import PropertyGallery from './components/property-gallery';
import PropertyAlerts from './components/property-alerts';
import PropertySummary from './components/property-summary';
import PropertyDetails from './components/property-details';
import PropertySidebar from './components/property-sidebar';
import PropertyModals from './components/property-modals';

// ---------------------------------------------------------

import {
  page,
  page_container,
  top_content_container,
  left,
  right,
  right_margin_top_600,
  bottom,
  grid_container,
  copyright_section,
  sticky_footer,
  section_header,
  calculator_section,
  breadcrumb_container,
  breadcrumbs,
  listing_courtesy_of
} from './styles.module.scss';

// ---------------------------------------------------------

import { ListingProvider } from './context';

// ---------------------------------------------------------

const ListingDetailPageTemplate = (props) => {
  const {
    interestRates,
    listingId: id,
    primaryDisplayAgent,
    secondaryDisplayAgent,
    isIdxListing,
    address,
    city,
    state,
    statusId,
    zip,
    price,
    mainPhoto,
    photos,
    marketingRemarks,
    similarProperties,
    updateNoteListings,
    canonicalUrl,
    openHouses,
    mlsCopyright,
    listingCommunity,
    nearbyCommunities,
    associatedAgent: associatedAgentProp,
    county,
    mapLocationExternalLink,
    latitude,
    longitude
  } = props;

  const truncatedListingName = truncateListingName(address, city, state, zip);

  // ---------------------------------------------------------

  const router = useRouter();
  const listing = useMemo(() => new Listing(id), [id]);
  const { setIsSibcyListing } = useSibcyListing();
  const { userInfo } = useUserInfo();
  const associatedAgentId = userInfo?.associatedAgent ?? userInfo?.agent;
  const {
    leadsPlusAgent,
    setLeadsPlusAgent,
    setLeadsPlusAgentToSessionStorage,
    getLeadsPlusAgentFromSessionStorage
  } = useLeadsPlusAgentData(associatedAgentProp);

  const [selectedArea, setSelectedArea] = useState('Photos');
  const [isMedium, setIsMedium] = useState(null);
  const [notificationModalIsOpen, setNotificationModalIsOpen] = useState(false);
  const [requestATourModalIsOpen, setRequestATourModalIsOpen] = useState(router.query.tourModal);
  const [preApprovalModalIsOpen, setPreApprovalModalIsOpen] = useState(false);
  const [notesModalIsOpen, setNotesModalIsOpen] = useState(false);
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [openHouseModalIsOpen, setOpenHouseModalIsOpen] = useState(false);
  const [displayAgents, setDisplayAgents] = useState([]);

  const communities_slides = useMemo(() => {
    return [listingCommunity].concat(nearbyCommunities || []).map((item) => ({
      excerpt: item?.excerpt,
      featuredImage: item?.featuredImage || {
        height: 960,
        url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
        width: 1440
      },
      linkLabel: item?.linkLabel || 'View Community',
      linkUrl: `/community/${item?.slug}`,
      title: item?.title
    }));
  }, [listingCommunity, nearbyCommunities]);

  const itemListData = useMemo(() => {
    const rawItems = [listingCommunity].concat(nearbyCommunities || []);
    const headerName =
      rawItems.length > 1
        ? `Explore ${listingCommunity?.title} & Surrounding Area`
        : 'Lifestyle in the Surrounding Area';

    return {
      name: headerName,
      items: rawItems
    };
  }, [listingCommunity, nearbyCommunities]);

  // Property-specific Q&A generated from listing data (AEO). The same items feed
  // the visible FaqBlock and the FAQPage JSON-LD so DOM and schema stay in sync.
  // Computed directly (not memoized): `props` is a fresh reference every render, so
  // a useMemo keyed on it never actually memoizes, and these builders are cheap.
  const faqItems = buildListingFaqItems(props);
  const faqSchema = generateFAQSchema(faqItems);

  const hasPrimaryAndSecondaryAgent = !!secondaryDisplayAgent && !!primaryDisplayAgent;
  const hasTour = statusId !== 6 && statusId !== 14;
  const hasMapLocationExternalLink = mapLocationExternalLink && latitude !== 0 && longitude !== 0;
  const hasOpenHouse = openHouses[0];

  useEffect(() => {
    setIsSibcyListing(!isIdxListing);

    return () => setIsSibcyListing(false);
  }, [isIdxListing, setIsSibcyListing]);

  useEffect(() => {
    // Handle agent data from session storage on mount
    if (associatedAgentProp) {
      setLeadsPlusAgentToSessionStorage(associatedAgentProp);
    } else {
      const agentData = getLeadsPlusAgentFromSessionStorage();
      if (agentData) {
        setLeadsPlusAgent(agentData);
      }
    }
  }, [
    associatedAgentProp,
    setLeadsPlusAgent,
    setLeadsPlusAgentToSessionStorage,
    getLeadsPlusAgentFromSessionStorage
  ]);

  useEffect(() => {
    if (!associatedAgentId) return;

    const fetchAssociatedAgentData = async (variables) => {
      const data = await execAssociatedAgentQuery(variables);
      setLeadsPlusAgent(data?.agentSearch?.items[0] ?? []);
    };

    const variables = { agentId: parseInt(associatedAgentId, 10) };
    fetchAssociatedAgentData(variables);
  }, [associatedAgentId, setLeadsPlusAgent]);

  useEffect(() => {
    if (router.query.openhouse && hasOpenHouse) {
      setOpenHouseModalIsOpen(true);
    }
  }, [router.query.openhouse, hasOpenHouse]);

  useEffect(() => {
    const updateViewCount = async () => {
      await listing.stats();
    };
    updateViewCount();

    const updateMedia = () => {
      setIsMedium(window.innerWidth < 1080);
    };

    updateMedia(); // Set initial value

    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateMedia);
      return () => window.removeEventListener('resize', updateMedia);
    }
  }, [listing]);

  // ---------------------------------------------------------

  const getBreadcrumbs = () => {
    const getBreadcrumbItems = () => {
      const items = [];
      if (state)
        items.push({
          label: state,
          url: '/browse',
          query: { search_type: 'all_listings', state }
        });

      if (zip)
        items.push({
          label: zip,
          url: '/results',
          query: {
            zip: `${zip}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      if (county)
        items.push({
          label: `${county} County`,
          url: '/results',
          query: {
            counties: `${county}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      if (city)
        items.push({
          label: city,
          url: '/results',
          query: {
            places: `${city}_${state}`,
            states: state,
            status: ['Active', 'Pending']
          }
        });
      items.push({ label: address, url: null });

      return items;
    };

    return (
      <div className={breadcrumbs}>
        {getBreadcrumbItems().map((item, index) => (
          <React.Fragment key={index}>
            <span>
              {item.url ? (
                <Link url={item.url} query={item.query} className={listing_courtesy_of}>
                  {item.label}
                </Link>
              ) : (
                <span className={listing_courtesy_of}>{item.label}</span>
              )}
            </span>
            {index < getBreadcrumbItems().length - 1 && (
              <span className={listing_courtesy_of}>•</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ---------------------------------------------------------

  const hasLeadsPlus = !!leadsPlusAgent?.agentId;

  const getDisplayAgents = async () => {
    if (userInfo?.agent) {
      const agentId = parseInt(userInfo?.agent, 10);

      const userAgentData = await graphql(
        gql`
          ${AGENT_BY_ID_QUERY}
        `,
        { agentId }
      );

      return [userAgentData?.agentSearch?.items[0]] ?? [];
    } else if (!isIdxListing) {
      if (hasPrimaryAndSecondaryAgent) {
        return [primaryDisplayAgent, secondaryDisplayAgent];
      } else {
        return [primaryDisplayAgent || secondaryDisplayAgent];
      }
    } else if (hasLeadsPlus) {
      return [leadsPlusAgent];
    } else {
      return [];
    }
  };

  useEffect(() => {
    const fetchDisplayAgents = async () => {
      const agents = await getDisplayAgents();
      setDisplayAgents(agents[0] ? agents : []);
    };
    fetchDisplayAgents();
  }, [userInfo?.agent, hasLeadsPlus]);

  return (
    <ListingProvider
      value={{
        ...props,
        hasTour,
        hasPrimaryAndSecondaryAgent,
        displayAgents,
        userInfo,
        hasMapLocationExternalLink
      }}
    >
      <Layout
        canonicalUrl={canonicalUrl}
        meta={{
          description: marketingRemarks,
          image: {
            alt: address,
            url: mainPhoto?.midSizeImageUrl
          },
          title: truncatedListingName
        }}
        listingDetails={props}
        specializedPageSchema="realEstateSchema"
        itemListData={itemListData}
        searchResultsSchema={props.similarPropertiesSchema}
        faqSchema={faqSchema}
      >
        <div className={page}>
          <div className={page_container}>
            <div className={grid_container}>
              <PropertyHeader />

              <PropertyGallery
                selectedArea={selectedArea}
                setSelectedArea={setSelectedArea}
                isMedium={isMedium}
              />
            </div>
            <div className={top_content_container}>
              <div className={left}>
                <PropertyAlerts selectedArea={selectedArea} setSelectedArea={setSelectedArea} />

                <PropertySummary
                  setNotificationModalIsOpen={setNotificationModalIsOpen}
                  setNotesModalIsOpen={setNotesModalIsOpen}
                  setShareModalIsOpen={setShareModalIsOpen}
                  setPreApprovalModalIsOpen={setPreApprovalModalIsOpen}
                  setRequestATourModalIsOpen={setRequestATourModalIsOpen}
                />

                <PropertyDetails />

                <FaqBlock title="Frequently Asked Questions" items={faqItems} />

                {communities_slides?.length > 0 && (
                  <div
                    onClick={() => pushDataLayerEvent('listing_community_click', router.asPath)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        pushDataLayerEvent('listing_community_click', router.asPath);
                    }}
                    role="button"
                    tabIndex="0"
                  >
                    <h2 className={section_header}>
                      {communities_slides?.length > 1
                        ? `Explore ${listingCommunity.title} & Surrounding Area`
                        : 'Lifestyle in the Surrounding Area'}
                    </h2>
                    <TopicCarousel
                      topic="community"
                      carouselId="custom-nearby-communities"
                      theme="ldp"
                      slidesCollection={{ cards: communities_slides }}
                    />
                  </div>
                )}
                <div className={breadcrumb_container}>{getBreadcrumbs()}</div>
              </div>

              <div
                className={classNames(right, {
                  [right_margin_top_600]: photos.length <= 1 && selectedArea === 'Photos'
                })}
              >
                <PropertySidebar setRequestATourModalIsOpen={setRequestATourModalIsOpen} />
              </div>
            </div>

            <Container>
              <div className={calculator_section}>
                <h2 className={section_header}>Monthly Cost</h2>
                <MortgageCalculatorEnhanced
                  price={price}
                  currentRates={interestRates}
                  lastUpdatedOn={interestRates?.lastUpdatedOn}
                  showCalculatorSelector={false}
                  condensed={true}
                  layout="inputs-focused"
                  hideVisual={true}
                  gridLayout={true}
                  title="Mortgage Calculator"
                  description={
                    <>
                      Enter your details for an estimated monthly payment.{' '}
                      <Link url="/calculators/affordability">
                        See how much house you can afford
                      </Link>
                    </>
                  }
                />
              </div>
            </Container>
          </div>

          <section className={bottom}>
            {similarProperties?.length ? (
              <SimilarProperties properties={similarProperties} address={address} />
            ) : null}
            <Container width="full" layout="Two thirds, one third" gutter="25">
              <SellWithSibcy />
              <PostList title="Must reads" shouldNotFetch={true} />
            </Container>
          </section>

          <Container>
            <section className={copyright_section}>
              <div>
                <Image
                  layout="fill"
                  image={{
                    height: '22px',
                    src: mlsCopyright.logoUrl,
                    width: '96px'
                  }}
                />
              </div>

              <div>{mlsCopyright.description}</div>
            </section>
          </Container>
        </div>

        <PropertyModals
          shareModalIsOpen={shareModalIsOpen}
          setShareModalIsOpen={setShareModalIsOpen}
          preApprovalModalIsOpen={preApprovalModalIsOpen}
          setPreApprovalModalIsOpen={setPreApprovalModalIsOpen}
          notesModalIsOpen={notesModalIsOpen}
          setNotesModalIsOpen={setNotesModalIsOpen}
          notificationModalIsOpen={notificationModalIsOpen}
          setNotificationModalIsOpen={setNotificationModalIsOpen}
          requestATourModalIsOpen={requestATourModalIsOpen}
          setRequestATourModalIsOpen={setRequestATourModalIsOpen}
          openHouseModalIsOpen={openHouseModalIsOpen}
          setOpenHouseModalIsOpen={setOpenHouseModalIsOpen}
          updateNoteListings={updateNoteListings}
        />

        {hasTour && (
          <div className={sticky_footer}>
            <div>
              <Button
                theme="red"
                onClick={() => {
                  pushDataLayerEvent('listing_request_a_tour_modal_click', router.asPath);
                  setRequestATourModalIsOpen(true);
                }}
                label="Request Tour"
                iconType="general"
                icon="clock"
              />
            </div>
          </div>
        )}
      </Layout>
    </ListingProvider>
  );
};

export default ListingDetailPageTemplate;
